import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Phone, Mail, MessageCircle, MapPin, Send, Instagram } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

export const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Construct WhatsApp message with form data
    const message = `Hello NA Realtors,

I would like to get in touch with you.

*Name:* ${formData.name}
*Email:* ${formData.email}
*Phone:* ${formData.phone}

*Message:*
${formData.message}`;

    // Redirect to WhatsApp
    window.open(`https://wa.me/918667644336?text=${encodeURIComponent(message)}`, '_blank');
    
    // Clear form after submission
    setFormData({ name: '', email: '', phone: '', message: '' });
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      content: '8667644336',
      action: () => window.open('tel:8667644336')
    },
    {
      icon: Mail,
      title: 'Email',
      content: 'contact@narealtors.in',
      action: () => window.open('mailto:contact@narealtors.in')
    },
    {
      icon: MessageCircle,
      title: 'WhatsApp',
      content: '8667644336',
      action: () => window.open('https://wa.me/918667644336', '_blank')
    },
    {
      icon: Instagram,
      title: 'Instagram',
      content: '@narealtors',
      action: () => window.open('https://www.instagram.com/', '_blank')
    }
  ];

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in-up">
          <div className="inline-block bg-[#D4AF37]/10 text-[#D4AF37] px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Get In Touch
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-[#0F1419] mb-6 playfair">
            Let's Start a Conversation
          </h2>
          <p className="text-gray-600 text-lg max-w-3xl mx-auto">
            Whether you're looking to buy, sell, or design your dream home, our team is ready to assist you.
            Reach out today!
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <Card className="p-8 border-2 border-gray-100 shadow-lg">
            <h3 className="text-2xl font-bold text-[#0F1419] mb-6 playfair">Send Us a Message</h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Enter your full name"
                  className="w-full border-gray-300 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="your.email@example.com"
                  className="w-full border-gray-300 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                />
              </div>
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number *
                </label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  placeholder="+91 XXXXX XXXXX"
                  className="w-full border-gray-300 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Message *
                </label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  placeholder="Tell us about your requirements..."
                  className="w-full border-gray-300 focus:border-[#D4AF37] focus:ring-[#D4AF37] resize-none"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-[#D4AF37] hover:bg-[#B8941F] text-white py-6 text-lg font-semibold"
              >
                <Send className="w-5 h-5 mr-2" />
                Send Message
              </Button>
            </form>
          </Card>

          {/* Contact Information */}
          <div className="space-y-6">
            <Card className="p-8 border-2 border-gray-100 shadow-lg">
              <h3 className="text-2xl font-bold text-[#0F1419] mb-6 playfair">Contact Information</h3>
              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <div
                    key={index}
                    onClick={info.action}
                    className="flex items-start space-x-4 p-4 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer group"
                  >
                    <div className="bg-[#D4AF37]/10 p-3 rounded-full group-hover:bg-[#D4AF37]/20 transition-colors">
                      <info.icon className="w-6 h-6 text-[#D4AF37]" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#0F1419] mb-1">{info.title}</p>
                      <p className="text-gray-600">{info.content}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Map or Additional Info */}
            <Card className="p-8 border-2 border-gray-100 shadow-lg bg-gradient-to-br from-[#0F1419] to-[#2d3e50]">
              <div className="text-white">
                <MapPin className="w-12 h-12 text-[#D4AF37] mb-4" />
                <h3 className="text-2xl font-bold mb-4 playfair">Visit Our Office</h3>
                <p className="text-gray-300 leading-relaxed mb-6">
                  Located in the heart of Coimbatore, serving clients across all major areas.
                  Schedule an appointment for personalized consultation.
                </p>
                <Button
                  onClick={() => window.open('https://wa.me/918667644336?text=I would like to schedule an office visit', '_blank')}
                  variant="outline"
                  className="border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-white"
                >
                  Schedule a Visit
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};